import React, { useState } from "react";
import { useUserContext } from "../../../providers/UserProvider";
import { useAgents } from "../../../hooks/useAgents/useAgents";
import { useDebounce } from "../../../hooks/useDebounce/useDebounce";
import { DEFAULT_DEBOUNCE, DEFAULT_PAGE_SIZE, MIN_SEARCH_QUERY_LENGTH } from "../../../constants/constants";
import { SearchInput } from "../../components/SearchInput/SearchInput";
import { LoadingIndicator } from "../../components/LoadingIndicator/LoadingIndicator";
import { ResetFilterPrompt } from "../../components/ResetFilterPrompt/ResetFilterPrompt";
import AgentsTable from "../../components/AgentsTable/AgentsTable";
import { PaginationControls } from "@optimizely/axiom";
import { User } from "../../../domain/User";
import { Sidebar } from "../../components/Sidebar/Sidebar";
import { AccessManagementUserForm } from "../../components/Access/AccessManagementUserForm/AccessManagementUserForm";
import { ActivationStatusesModal } from "../../components/ActivationStatusesTable/ActivationStatusesModal";

import styles from "./AgentsPage.module.scss";

export const AgentsPage = () => {
    const { organizationId = "" } = useUserContext();
    const [selectedAgent, setSelectedAgent] = useState<User | null>(null);
    const [showActivationStatusesModal, setShowActivationStatusesModal] = useState(false);

    const {
        debouncedValue: debouncedSearchQuery,
        value: searchQuery,
        setValue: setSearchQuery
    } = useDebounce<string>("", DEFAULT_DEBOUNCE);

    const { agents, isLoading, error, currentPage, setCurrentPage, totalCount } = useAgents({
        organizationId,
        query: debouncedSearchQuery.length >= MIN_SEARCH_QUERY_LENGTH ? debouncedSearchQuery : ""
    });

    const totalPages = Math.ceil(totalCount / DEFAULT_PAGE_SIZE);
    const showPagination = totalPages > 1;
    const noAgents = !isLoading && !agents?.length && debouncedSearchQuery.length < MIN_SEARCH_QUERY_LENGTH;

    const goToPage = (page: number) => {
        setCurrentPage(page);
    };

    const handleViewDetails = (agent: User) => {
        setSelectedAgent(agent);
    };

    const handleCloseSidebar = () => {
        setSelectedAgent(null);
    };

    if (error) return <div className="push-double--top">Error fetching virtual teammates list.</div>;

    return (
        <div className={`list-page ${styles["agents-page"]}`}>
            <div className={styles["agents-page__bar"]}>
                <div className={styles["agents-page__search"]}>
                    <SearchInput
                        placeholder="Search virtual teammates by name or email..."
                        type="text"
                        value={searchQuery}
                        onChange={({ value }) => {
                            if (currentPage !== 1) {
                                setCurrentPage(1);
                            }
                            setSearchQuery(value);
                        }}
                        disabled={noAgents}
                    />
                </div>
            </div>

            {debouncedSearchQuery.length >= MIN_SEARCH_QUERY_LENGTH && !agents?.length && !isLoading && (
                <ResetFilterPrompt
                    onClick={() => setSearchQuery("")}
                    prompt="No virtual teammates match your search."
                />
            )}

            {noAgents && <div>No virtual teammates found in your organization.</div>}

            {isLoading ? (
                <LoadingIndicator height="100%" type="spinner" />
            ) : (
                <>
                    <AgentsTable agentList={agents} onViewDetails={handleViewDetails} />
                    {showPagination && (
                        <PaginationControls
                            className="anchor--bottom"
                            currentPage={currentPage}
                            goToPage={goToPage}
                            totalPages={totalPages}
                        />
                    )}
                </>
            )}

            {!!selectedAgent && (
                <Sidebar heading="Virtual Teammate" isOpen onCancel={handleCloseSidebar} noTransform>
                    <AccessManagementUserForm
                        onCancel={handleCloseSidebar}
                        organizationId={organizationId}
                        showModal={setShowActivationStatusesModal}
                        user={selectedAgent}
                    />
                </Sidebar>
            )}

            {showActivationStatusesModal && <ActivationStatusesModal showModal={setShowActivationStatusesModal} />}
        </div>
    );
};

AgentsPage.displayName = "AgentsPage";
